import { ChangeEvent, RefObject } from "react";
import { Icon } from "../../Common/Icons/LucideIcons";
import {
  ButtonRow,
  HiddenFileInput,
  RightButtonGroup,
  SendButton,
} from "./__styles__/ChatInput";
import {
  CancelButton,
  FileAttachmentButton,
  LoadingSpinner,
  MicrophoneButton,
  RecordingTimer,
} from "./__styles__/ChatInputButtonGroup";

export type ChatInputButtonGroupProps = {
  // Form state
  disabled?: boolean;
  hasContent: boolean;
  hasFiles: boolean;

  // Audio recording state
  audioRecordingStatus: "idle" | "permission-pending" | "recording";
  recordingTime: number;
  isTranscribing: boolean;

  // File handling
  fileInputRef: RefObject<HTMLInputElement | null>;
  onFileButtonClick: () => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;

  // Audio recording handlers
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  onCancelRecording?: () => void;
};

const ChatInputButtonGroup = ({
  disabled = false,
  hasContent,
  hasFiles,
  audioRecordingStatus,
  recordingTime,
  isTranscribing,
  fileInputRef,
  onFileButtonClick,
  onFileChange,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
}: ChatInputButtonGroupProps) => {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const renderLeftButton = () => {
    // Show file input when idle, cancel button when recording/transcribing
    if (audioRecordingStatus === "idle" && !isTranscribing) {
      return (
        <FileAttachmentButton
          type="button"
          disabled={disabled}
          onClick={onFileButtonClick}
          aria-label="Add attachment"
        >
          <Icon iconName="plus" color="contentSecondary" size={16} />
        </FileAttachmentButton>
      );
    }

    return (
      <CancelButton
        type="button"
        onClick={onCancelRecording || (() => {})}
        aria-label="Cancel recording"
        disabled={isTranscribing}
      >
        <Icon iconName="x" color="contentSecondary" size={16} />
      </CancelButton>
    );
  };

  const renderRightButtons = () => {
    if (audioRecordingStatus === "idle" && !isTranscribing) {
      return (
        <>
          {/* Microphone button */}
          <MicrophoneButton
            type="button"
            onClick={onStartRecording || (() => {})}
            aria-label="Start voice recording"
          >
            <Icon iconName="mic" color="contentSecondary" size={16} />
          </MicrophoneButton>

          {/* Send button */}
          <SendButton
            type="submit"
            disabled={disabled || (!hasContent && !hasFiles)}
            aria-label="Send message"
          >
            <Icon
              iconName="arrow-right"
              color="contentPrimaryInverted"
              size={16}
            />
          </SendButton>
        </>
      );
    }

    if (audioRecordingStatus === "recording") {
      return (
        <>
          {/* Recording timer */}
          <RecordingTimer>{formatTime(recordingTime)}</RecordingTimer>

          {/* Submit button */}
          <SendButton
            type="button"
            onClick={onStopRecording}
            aria-label="Stop and submit recording"
          >
            <Icon iconName="check" color="contentPrimaryInverted" size={12} />
          </SendButton>
        </>
      );
    }

    // Loading state when transcribing
    return (
      <SendButton type="button" aria-label="Transcribing" disabled>
        <LoadingSpinner />
      </SendButton>
    );
  };

  return (
    <ButtonRow>
      {renderLeftButton()}

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        multiple
        onChange={onFileChange}
      />

      <RightButtonGroup>{renderRightButtons()}</RightButtonGroup>
    </ButtonRow>
  );
};

export default ChatInputButtonGroup;
